import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Link2, RefreshCw } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function Registry() {
  const { t } = useI18n();
  const [records, setRecords] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/registry").then(({ data }) => setRecords(data));
    api.get("/registry/verify").then(({ data }) => setResult(data));
  };
  useEffect(() => { load(); }, []);

  const verify = async () => {
    setLoading(true);
    try { const { data } = await api.get("/registry/verify"); setResult(data); } finally { setLoading(false); }
  };

  return (
    <div>
      <section className="border-b border-border bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 civic-grid opacity-10" />
        <div className="relative max-w-5xl mx-auto px-4 py-14">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary-foreground/70">Blockchain-style ledger</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-black tracking-tighter">{t("registry.title")}</h1>
          <p className="mt-4 text-primary-foreground/85 max-w-2xl leading-relaxed">{t("registry.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <Card className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            {result && (
              <div data-testid="verify-result" className={`flex items-center gap-2 font-heading text-xl font-bold ${result.valid ? "text-emerald-600" : "text-destructive"}`}>
                {result.valid ? <ShieldCheck className="h-6 w-6" /> : <ShieldAlert className="h-6 w-6" />}
                {result.valid ? t("registry.valid") : `${t("registry.invalid")} (#${result.broken_at})`}
              </div>
            )}
            {result && <div className="text-sm text-muted-foreground mt-1 font-mono">{result.total_records} {t("registry.records")} · {t("registry.head")}: {result.head_hash.slice(0, 24)}…</div>}
          </div>
          <Button data-testid="verify-btn" onClick={verify} disabled={loading}><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />{t("registry.verifyBtn")}</Button>
        </Card>

        <div className="mt-8 space-y-2">
          {records.map((r) => (
            <div key={r.index} data-testid={`ledger-${r.index}`} className="rounded-md border border-border bg-card p-4 font-mono text-xs overflow-hidden">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded bg-primary text-primary-foreground font-bold not-italic">#{r.index}</span>
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{new Date(r.timestamp).toLocaleString()}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                <div><span className="text-foreground font-semibold">{t("registry.prevHash")}: </span><span className="break-all">{r.prev_hash.slice(0, 40)}…</span></div>
                <div><span className="text-foreground font-semibold">{t("registry.hash")}: </span><span className="break-all text-primary">{r.hash.slice(0, 40)}…</span></div>
              </div>
            </div>
          ))}
          {records.length === 0 && <p className="text-muted-foreground text-sm">{t("home.empty")}</p>}
        </div>
      </div>
    </div>
  );
}
